<template>
  <main
    id="wizco"
    :key="wizco.activeStepUuid"
    :style="{ 'background-color': wizco.activeStep.content?.backgroundColor }"
  >
    <div class="calloutWrapper">
      <Transition name="callout">
        <HeaderCallout
          v-if="
            wizco.activeStep.content &&
            wizco.activeStep.content?.callout &&
            wizco.activeStep.content?.callout[0]
          "
          :callout-data="wizco.activeStep.content.callout[0]"
        />
      </Transition>
    </div>

    <div v-if="wizco.activeStep.is_startpage" class="text-center">
      <p>Einstellungen vom:</p>
      <h1>
        {{ wizco.settings.content.name }}
        <small class="text-m">v{{ parseFloat(wizco.settings.content.version).toFixed(1) }}</small>
      </h1>
    </div>
    <div v-else class="main-wizard">
      <div :class="`component--${wizco.activeStep.content?.component}`" class="step-wrapper">
        <HeaderProgressBar
          v-if="
            wizco.activeStep.content &&
            wizco.activeStep.content?.progressbar &&
            wizco.activeStep.content?.progressbar[0]
          "
          :progress-bar-data="wizco.activeStep.content.progressbar[0]"
        />

        <HeaderInline
          v-if="
            wizco.activeStep.content &&
            wizco.activeStep.content?.header &&
            wizco.activeStep.content?.header[0]
          "
          :header-data="wizco.activeStep.content.header[0]"
        />
        <div class="step-content">
          <component
            :is="wizco.activeStep.content?.component"
            :step-data="wizco.activeStep.content"
            :step-uuid="wizco.activeStep.uuid"
          />
        </div>
      </div>
      <HeaderBackground
        v-if="
          wizco.activeStep.content &&
          wizco.activeStep.content?.header &&
          wizco.activeStep.content?.header[0]
        "
        :image="wizco.activeStep.content.header[0].desktop_image"
        :is-mobile="false"
      />
    </div>

    <UspFooter />

    <PflegeModal v-model="showAlertModal" heading="Entschuldigung, es ist ein Fehler aufgetreten">
      <div v-html="alertModalContent"></div>
    </PflegeModal>
  </main>
</template>

<script setup>
import { useStoryblokBridge } from '@storyblok/vue'
import { useWizcoStore } from './stores/wizco'
import { useAlert } from './composable/useAlert'
import HeaderCallout from './components/header/HeaderCallout.vue'
import HeaderBackground from './components/header/HeaderBackground.vue'
import HeaderInline from './components/header/HeaderInline.vue'
import HeaderProgressBar from './components/header/HeaderProgressBar.vue'
import UspFooter from './components/footer/UspFooter.vue'
import { toRef, provide } from 'vue'

const props = defineProps({
  uuid: {
    type: String,
    required: true
  },
  isEditMode: {
    type: String,
    required: false,
    default: 'false'
  },
  trackingToken: {
    type: String,
    required: false,
    default: ''
  },
  sessionData: {
    type: String,
    required: false,
    default: null
  },
  decisionContexts: {
    type: String,
    required: false,
    default: null
  },
  authTokenContext: {
    type: String,
    required: false,
    default: ''
  },
  customerStatus: {
    type: String,
    require: false,
    default: 'Neukunde',
    validator(value) {
      return ['Neukunde', 'Bestandskunde'].includes(value)
    }
  }
})

const { showAlertModal, alertModalContent } = useAlert()

const wizco = useWizcoStore()
wizco.trackingToken = toRef(() => props.trackingToken)
wizco.decisionContexts = JSON.parse(props.decisionContexts)
wizco.authTokenContext = props.authTokenContext
wizco.customerStatus = props.customerStatus
wizco.isEditMode = props.isEditMode === 'true'

if (wizco.isEditMode) {
  await wizco.getStep(props.uuid)
  let wizardIdentifier = wizco.activeStep.full_slug.replace(wizco.activeStep.slug, '')
  if (wizco.activeStep.is_startpage) {
    // settings itself
    wizardIdentifier = wizco.activeStep.uuid
  }
  await wizco.getSettings(wizardIdentifier)
  if (wizco.activeStep.is_startpage) {
    // settings itself
    useStoryblokBridge(wizco.settings.id, (story) => {
      wizco.settings = story
    })
  }
} else {
  await wizco.getSettings(props.uuid)
  await wizco.getStep(wizco.settings.content.firstStep)
}
wizco.start(props.sessionData)
provide('track', () => {})
</script>

<style>
#wizco {
  box-sizing: border-box;
  --padding-inline: 1rem;
  --wk-input-height: 2.33rem;
  min-height: var(--minHeight, inherit);
}

#wizco .main-wizard {
  height: 100%;
}

#wizco .main-wizard .step-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: flex-start;
  padding: 1.33rem var(--padding-inline) 0;
  min-height: 100%;
}

#wizco .main-wizard .step-wrapper.component--WizcoStepDynamic {
  gap: 0;
}

#wizco .main-wizard .step-content {
  width: 100%;
}

@media (min-width: 576px) {
  #wizco {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  #wizco .main-wizard {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  #wizco .main-wizard .step-wrapper {
    height: auto;
    max-width: 32.75rem;
    flex-grow: 1;
  }
}

@media (max-width: 576px) {
  #wizco .base-button {
    width: 100%;
  }
}

/* Popper */
.popper {
  transition: background var(--time2) ease-in-out;
  background: var(--popper-theme-background-color);
  padding: var(--popper-theme-padding);
  color: var(--popper-theme-text-color);
  border-radius: var(--popper-theme-border-radius);
  border-width: var(--popper-theme-border-width);
  border-style: var(--popper-theme-border-style);
  border-color: var(--popper-theme-border-color);
  box-shadow: var(--popper-theme-box-shadow);
  z-index: 6;
}

.popper:hover,
.popper:hover > #arrow::before {
  background: var(--popper-theme-background-color-hover);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.popper #arrow,
.popper #arrow::before {
  transition: background 250ms ease-in-out;
  position: absolute;
  width: calc(10px - var(--popper-theme-border-width, 0px));
  height: calc(10px - var(--popper-theme-border-width, 0px));
  background: var(--popper-theme-background-color);
}

.popper #arrow {
  visibility: hidden;
}

.popper #arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
}

.popper[data-popper-placement^='top'] > #arrow {
  bottom: -5px;
}

.popper[data-popper-placement^='top'] > #arrow::before {
  border-right: var(--popper-theme-border-width) var(--popper-theme-border-style)
    var(--popper-theme-border-color);
  border-bottom: var(--popper-theme-border-width) var(--popper-theme-border-style)
    var(--popper-theme-border-color);
}

.popper[data-popper-placement^='bottom'] > #arrow {
  top: -5px;
}

.popper[data-popper-placement^='bottom'] > #arrow::before {
  border-left: var(--popper-theme-border-width) var(--popper-theme-border-style)
    var(--popper-theme-border-color);
  border-top: var(--popper-theme-border-width) var(--popper-theme-border-style)
    var(--popper-theme-border-color);
}

.popper[data-popper-placement^='left'] > #arrow {
  right: -5px;
}

.popper[data-popper-placement^='left'] > #arrow::before {
  border-right: var(--popper-theme-border-width) var(--popper-theme-border-style)
    var(--popper-theme-border-color);
  border-top: var(--popper-theme-border-width) var(--popper-theme-border-style)
    var(--popper-theme-border-color);
}

.popper[data-popper-placement^='right'] > #arrow {
  left: -5px;
}
</style>
